/**
 * PicksDataTab Component
 * Main component orchestrating the Picks Data Dashboard
 * Provides infinite scroll, filtering, sorting, and drill-down into individual picks
 */

import { useAdminPicks } from '@hooks/useAdminPicks';
import { useAdminPicksCharts } from '@hooks/useAdminPicksCharts';
import { useAdminPicksMetrics } from '@hooks/useAdminPicksMetrics';
import type {
  GroupBy,
  ParlaySort,
  PicksFilters,
  SortBy,
  SortDirection,
  ViewMode,
} from '@server/services/admin/adminPicksTypes.ts';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ChartsSection } from './PicksData/ChartsSection';
import { MetricCardsSection } from './PicksData/MetricCardsSection';
import { PickDetailModal } from './PicksData/PickDetailModal';
import { PicksFiltersSection } from './PicksData/PicksFiltersSection';
import { PicksTableSection } from './PicksData/PicksTableSection';

interface PicksDataTabProps {
  /** Filters to open with, such as the prop type an AI Accuracy row links to. */
  initialFilters?: PicksFilters;
  onOpenAiAccuracy: () => void;
}

export function PicksDataTab({ initialFilters = {}, onOpenAiAccuracy }: PicksDataTabProps) {
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>('leg');
  const [sortBy, setSortBy] = useState<SortBy | ParlaySort>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<PicksFilters>(initialFilters);
  const [selectedPickId, setSelectedPickId] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<{
    propTypes: string[];
    sportsbooks: string[];
    configVariations: string[];
  } | null>(null);

  // Hooks
  const { picks, parlays, hasMore, total, loading, loadingMore, error, loadMore, refetch } =
    useAdminPicks({
      viewMode,
      groupBy,
      sortBy,
      sortDirection,
      filters,
      limit: 50,
    });

  const {
    metrics,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useAdminPicksMetrics({ viewMode, groupBy, filters });

  const {
    data: chartData,
    loading: chartsLoading,
    error: chartsError,
    refetch: refetchCharts,
  } = useAdminPicksCharts({ filters });

  // Intersection Observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, loadMore]);

  // Fetch filter options on mount
  useEffect(() => {
    async function fetchFilterOptions() {
      try {
        const response = await fetch('/api/admin/picks/filter-options', {
          credentials: 'include',
        });

        if (response.ok) {
          const options = await response.json();
          setFilterOptions(options);
        }
      } catch (_err) {}
    }

    fetchFilterOptions();
  }, []);

  /**
   * Handle sort change
   * If sorting same field, toggle direction
   * If sorting different field, reset to DESC
   */
  const handleSort = (field: SortBy | ParlaySort) => {
    if (sortBy === field) {
      // Toggle direction
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // New field, default to DESC
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  /**
   * Handle refresh (refetch picks, metrics, and charts)
   */
  const handleRefresh = () => {
    refetch();
    refetchMetrics();
    refetchCharts();
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <header className="space-y-4">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-theme-text">Picks Data</h1>
          <p className="text-sm text-theme-text-secondary">
            Browse and analyze all AI-generated picks
            {total > 0 && ` (${total.toLocaleString()} total)`}
          </p>
          <button
            type="button"
            onClick={onOpenAiAccuracy}
            className="inline-block text-sm text-theme-primary hover:underline"
          >
            ← View AI Calibration Metrics
          </button>
        </div>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 rounded-lg bg-theme-primary-fill px-4 py-2 text-sm font-medium text-theme-on-primary-fill hover:bg-theme-primary-fill/90 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-theme-danger bg-theme-danger/10 p-4">
          <p className="text-sm text-theme-danger">{error}</p>
        </div>
      )}

      {metricsError && (
        <div className="rounded-lg border border-theme-danger bg-theme-danger/10 p-4">
          <p className="text-sm text-theme-danger">Failed to load metrics: {metricsError}</p>
        </div>
      )}

      {chartsError && (
        <div className="rounded-lg border border-theme-danger bg-theme-danger/10 p-4">
          <p className="text-sm text-theme-danger">Failed to load charts: {chartsError}</p>
        </div>
      )}

      {/* Metric Cards */}
      <div className="flex justify-center">
        <div className="w-full max-w-7xl">
          <MetricCardsSection metrics={metrics} loading={metricsLoading} />
        </div>
      </div>

      {/* Charts */}
      <ChartsSection data={chartData} loading={chartsLoading} />

      {/* Filters */}
      <PicksFiltersSection
        viewMode={viewMode}
        filters={filters}
        filterOptions={filterOptions}
        onViewModeChange={setViewMode}
        onFiltersChange={setFilters}
      />

      {/* Group By Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg border border-theme-border bg-theme-surface p-1">
          <button
            type="button"
            onClick={() => {
              setGroupBy('leg');
              setSortBy('created_at');
              setSortDirection('desc');
            }}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              groupBy === 'leg'
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'text-theme-text-secondary hover:text-theme-text'
            }`}
          >
            Leg View
          </button>
          <button
            type="button"
            onClick={() => {
              setGroupBy('parlay');
              setSortBy('created_at');
              setSortDirection('desc');
            }}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              groupBy === 'parlay'
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'text-theme-text-secondary hover:text-theme-text'
            }`}
          >
            Parlay View
          </button>
        </div>
      </div>

      {/* Table */}
      <PicksTableSection
        picks={picks}
        parlays={parlays}
        groupBy={groupBy}
        sortBy={sortBy}
        sortDirection={sortDirection}
        loading={loading}
        onSort={handleSort}
        onRowClick={setSelectedPickId}
      />

      {/* Infinite Scroll Trigger */}
      {hasMore && !loading && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {loadingMore ? (
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-theme-border border-t-theme-primary"></div>
          ) : (
            <p className="text-sm text-theme-text-secondary">Scroll to load more...</p>
          )}
        </div>
      )}

      {/* End of List */}
      {!hasMore && picks.length > 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-theme-text-secondary">
            End of results ({picks.length} of {total} loaded)
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPickId && (
        <PickDetailModal
          pickId={selectedPickId}
          onClose={() => setSelectedPickId(null)}
          onShowLinkedPicks={(linked) => {
            setFilters(linked);
            setSelectedPickId(null);
          }}
        />
      )}
    </div>
  );
}
